import { useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useProfilePhotoSync — subscribe once per dashboard. When the user
 * uploads (or removes) a profile picture we broadcast the
 * `profile-photo-updated` CustomEvent from wherever the change was made
 * (UserAvatar camera overlay OR ProfilePage). This hook does BOTH:
 *   1. Immediately patches the local `user` state with the new photo so
 *      the avatars re-render even before the network round-trip returns.
 *   2. Re-fetches `/api/auth/me` in the background so every derived field
 *      (auth token expiry, notifications badge, etc.) stays in sync.
 * The result: whichever avatar the user opens the picker from — sidebar
 * chip, topbar chip, welcome header, PM/Tenant profile button — every
 * other avatar refreshes in real time, no page reload required.
 */
export function useProfilePhotoSync(setUser) {
  useEffect(() => {
    if (typeof setUser !== 'function') return;
    const onSync = async (e) => {
      const nextPhoto = e?.detail?.photo ?? null;
      // 1. Optimistic local patch — every avatar reacts within one frame.
      setUser((prev) => (
        prev
          ? { ...prev, profile_photo: nextPhoto, picture: nextPhoto }
          // If auth-context user hasn't hydrated yet, seed a minimal
          // stub so downstream avatars still show the new photo.
          : { profile_photo: nextPhoto, picture: nextPhoto }
      ));
      // 2. Reconcile with server so any derived fields refresh too.
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (res.ok) setUser(await res.json());
      } catch (_) { /* silent — the optimistic patch is already applied */ }
    };
    window.addEventListener('profile-photo-updated', onSync);
    return () => window.removeEventListener('profile-photo-updated', onSync);
  }, [setUser]);
}
